import { useState, useEffect, useCallback } from "react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import { useAuth } from "../auth/Authprovider";
import { AdminProduct, ProductFilters } from "../types/product.types";

export type UpdatedStatusFilter = "all" | "pending" | "approved" | "rejected";

interface UseProductListOptions {
  role?: "admin" | "seller";
  activeTab?: string;
  searchTerm?: string;
  autoFetch?: boolean;
  /** Only applied when activeTab is `updated-status` */
  updatedStatusFilter?: UpdatedStatusFilter;
  /** Optional approval status filter for list-style tabs */
  approvalStatusFilter?: "all" | "pending" | "approved" | "rejected";
}

export function useProductList(options: UseProductListOptions = {}) {
  const { userRole, isLoading: authLoading, isAuthenticated } = useAuth();

  const actualRole = options.role || userRole || "admin";
  const isAdmin = actualRole === "admin";
  const activeTab = options.activeTab || "all";
  const searchTerm = options.searchTerm || "";
  const autoFetch = options.autoFetch !== false;
  const updatedStatusFilter = options.updatedStatusFilter ?? "all";
  const approvalStatusFilter = options.approvalStatusFilter ?? "all";

  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const fetchProducts = useCallback(
    async (signal?: AbortSignal, forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        const filters: ProductFilters = {};

        if (isAdmin) {
          if (activeTab === "admin-created") {
            filters.adminCreated = true;
          } else if (activeTab === "seller-submitted") {
            filters.adminCreated = false;
          } else if (activeTab === "pending") {
            filters.approvalStatus = "pending";
          } else if (activeTab === "approved") {
            filters.approvalStatus = "approved";
          } else if (activeTab === "rejected") {
            filters.approvalStatus = "rejected";
          } else if (activeTab === "pending-updates") {
            filters.hasPendingUpdate = true;
          } else if (activeTab === "updated-status") {
            filters.sellerUpdateWorkflowList = "1";
            filters.sellerUpdateReviewOutcome = updatedStatusFilter;
          }
        } else {
          if (activeTab === "pending") {
            filters.approvalStatus = "pending";
          } else if (activeTab === "approved") {
            filters.approvalStatus = "approved";
          } else if (activeTab === "rejected") {
            filters.approvalStatus = "rejected";
          } else if (activeTab === "draft") {
            filters.status = "draft";
          } else if (activeTab === "updated-status") {
            filters.sellerUpdateWorkflowList = "1";
            filters.sellerUpdateReviewOutcome = updatedStatusFilter;
          }
        }

        if (searchTerm) {
          filters.search = searchTerm;
        }
        if (
          approvalStatusFilter !== "all" &&
          activeTab !== "pending" &&
          activeTab !== "approved" &&
          activeTab !== "rejected"
        ) {
          filters.approvalStatus = approvalStatusFilter;
        }
        filters.page = pagination.page;
        filters.limit = pagination.limit;

        const response = isAdmin
          ? await adminService.getAdminProducts(filters, forceRefresh)
          : await sellerService.getSellerProducts(filters, forceRefresh);

        if (signal?.aborted) return;

        if (response.success) {
          setProducts(response.data.products || []);
          if (response.data.total !== undefined) {
            setPagination((prev) => ({
              ...prev,
              total: response.data.total || 0,
              totalPages: Math.ceil((response.data.total || 0) / prev.limit),
              page: response.data.page || prev.page,
              limit: response.data.limit || prev.limit,
            }));
          }
        } else {
          setError(response.message || "Failed to fetch products");
        }
      } catch (err) {
        if (!signal?.aborted) {
          console.error("Error fetching products:", err);
          setError("Failed to fetch products. Please try again.");
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      isAdmin,
      activeTab,
      searchTerm,
      updatedStatusFilter,
      approvalStatusFilter,
      pagination.page,
      pagination.limit,
    ]
  );

  const refresh = useCallback(async () => {
    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    await fetchProducts(newAbortController.signal, true);
  }, [abortController, fetchProducts]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeTab, searchTerm, updatedStatusFilter, approvalStatusFilter]);

  // Auto-fetch when enabled
  useEffect(() => {
    if (!autoFetch) return;
    if (authLoading || !isAuthenticated) return;

    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const loadData = async () => {
      const { getAccessToken } = await import("@b2b/services");
      const token = await getAccessToken();
      if (!token) {
        if (isMounted) {
          setTimeout(() => loadData(), 500);
        }
        return;
      }

      if (!isMounted) return;

      if (abortController) {
        abortController.abort();
      }

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      // Debounce search requests
      timeoutId = setTimeout(
        () => {
          if (isMounted) {
            fetchProducts(newAbortController.signal, false);
          }
        },
        searchTerm ? 500 : 0
      );
    };

    loadData();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [
    activeTab,
    searchTerm,
    updatedStatusFilter,
    approvalStatusFilter,
    pagination.page,
    pagination.limit,
    autoFetch,
    authLoading,
    isAuthenticated,
    fetchProducts,
  ]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, []);

  return {
    products,
    loading,
    error,
    pagination,
    setPagination,
    refresh,
    fetchProducts,
  };
}
